import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useRBAC } from '../../../contexts/RBACContext';
import { useAuth } from '../../../contexts/AuthContext';
import { userAPI } from '../../../services/api';
import { roleAPI } from '../../../services/rbacApi';
import {
  CardGridSkeleton,
  DataViewLoadingContainer,
  TableSkeleton,
} from '../Common/DataViewLoading';
import PermissionGuard from '../../Common/PermissionGuard';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';
import SearchableRoleDropdown from '../../Common/SearchableRoleDropdown';
import UserCreateForm from './UserCreateForm';
import UserEditForm from './UserEditForm';
import UserLocationManagement from './UserLocationManagement';



const UserManagement = () => {
  const {
    roles,
    userRoles,
    error,
    filters,
    pagination,
    fetchUserRoles,
    assignUserRole,
    revokeUserRole,
    setFilters,
    updatePagination,
    clearError,
    rbacUtils
  } = useRBAC();

  const { user: currentUser, isAuthenticated, isOnlyAdmin, isSuperAdmin } = useAuth();
  const isAdminOnly = typeof isOnlyAdmin === 'function'
    ? isOnlyAdmin()
    : (typeof isSuperAdmin === 'function' ? !isSuperAdmin() : Boolean(isOnlyAdmin));

  const isSuperAdminAccount = (user) => {
    if (!user) return false;
    if (String(user.primaryRole || '').toUpperCase() === 'SUPER_ADMIN') return true;
    if (Array.isArray(user.roles) && user.roles.some(r => String(r || '').toUpperCase() === 'SUPER_ADMIN')) return true;
    return false;
  };

  // Get initial view from localStorage or default to 'card'
  const getInitialView = () => {
    try {
      return localStorage.getItem('userManagementView') || 'card';
    } catch (error) {
      return 'card';
    }
  };

  const [view, setView] = useState(getInitialView()); // 'card', 'list', 'edit', 'create'
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [isMounted, setIsMounted] = useState(false);
  const [filterRoles, setFilterRoles] = useState([]);
  const [resolvedRolesByCode, setResolvedRolesByCode] = useState({});
  const [additionalRolesMenu, setAdditionalRolesMenu] = useState(null);
  const additionalRolesMenuRef = useRef(null);
  const additionalRolesTriggerRef = useRef(null);
  const fetchAbortRef = useRef(null);
  const searchDebounceRef = useRef(null);

  // Reset filters once on mount; initialize pagination with limit so we don't re-fetch when limit appears later
  useEffect(() => {
    setSearchInput('');
    setFilters({
      searchTerm: '',
      userRole: '',
      userStatus: ''
    });
    updatePagination('users', { current: 1, limit: 20, total: 0, pages: 0 });
    setIsMounted(true);

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      if (fetchAbortRef.current) fetchAbortRef.current.abort();
      setSearchInput('');
      setFilters({
        searchTerm: '',
        userRole: '',
        userStatus: ''
      });
      setIsMounted(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount/unmount only

  // Save view preference to localStorage
  const handleViewChange = (newView) => {
    setAdditionalRolesMenu(null);
    setView(newView);
    try {
      localStorage.setItem('userManagementView', newView);
    } catch (error) {
      console.warn('Failed to save view preference');
    }
  };

  // Debounced search — merge only searchTerm so we don't recreate the handler on every filter change
  const debouncedSearch = useCallback((value) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFilters({ searchTerm: value });
      updatePagination('users', { current: 1 });
    }, 500);
  }, [setFilters, updatePagination]);

  // Fetch users with pagination; abort in-flight requests so low-bandwidth clients don't stack loaders
  const fetchUsers = useCallback(async (page = 1, limit = 20, searchTerm = '', roleFilter = '', statusFilter = '') => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setUsersLoading(true);
    setUsersError(null);

    try {
      const params = {
        page,
        limit,
        includeEmployeeId: 'true',
        excludePortalRoles: 'true',
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { status: statusFilter })
      };

      const response = await userAPI.getUsers(params, { signal: controller.signal });

      const responseData = response.data?.data || response.data || {};
      const usersData = responseData.users || [];
      const paginationData = responseData.pagination || {};

      const processedUsers = usersData.map(user => ({
        ...user,
        firstName: user.firstName || user.profile?.firstName || '',
        lastName: user.lastName || user.profile?.lastName || '',
        id: user._id || user.id,
        primaryRole: user.primaryRole,
        roles: Array.isArray(user.roles) ? user.roles : []
      }));

      setUsers(processedUsers);
      setAdditionalRolesMenu(null);

      // Only update totals — avoid rewriting current/limit (that re-triggered the fetch effect)
      updatePagination('users', {
        total: paginationData.total || processedUsers.length,
        pages: paginationData.pages || Math.ceil((paginationData.total || processedUsers.length) / limit)
      });
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      setUsersError(error.response?.data?.message || 'Failed to fetch users');
    } finally {
      if (fetchAbortRef.current === controller) {
        setUsersLoading(false);
      }
    }
  }, [updatePagination]);

  useEffect(() => {
    let isActive = true;

    const loadFilterRoles = async () => {
      try {
        const response = await roleAPI.getAssignableRoles();
        const roleOptions = response.data?.data?.roles || [];

        if (isActive) {
          const PORTAL_ROLES = new Set(['EXTERNAL_COMPANY', 'JUDGE', 'AUDITOR']);
          setFilterRoles(
            roleOptions.filter((r) => !PORTAL_ROLES.has(String(r.code || '').toUpperCase()))
          );
        }
      } catch (error) {
        console.error('Failed to load filter roles:', error);
      }
    };

    loadFilterRoles();

    return () => {
      isActive = false;
    };
  }, []);

  // resolvedRolesByCode is kept as an empty map — roles are resolved via the
  // already-loaded `roles` and `filterRoles` lists. Individual per-role API
  // calls were removed to eliminate the N+1 request pattern that caused
  // slowness when the user list loaded.

  // Fetch users when pagination or filters change - but ONLY after mount is complete and auth is ready
  useEffect(() => {
    if (isAuthenticated && isMounted && pagination?.users?.current) {
      fetchUsers(
        pagination.users.current || 1,
        pagination.users.limit || 20,
        filters?.searchTerm || '',
        filters?.userRole || '',
        filters?.userStatus || ''
      );
    }
  }, [
    isAuthenticated,
    isMounted,
    pagination?.users?.current,
    pagination?.users?.limit,
    filters?.searchTerm,
    filters?.userRole,
    filters?.userStatus,
    fetchUsers
  ]);

  const getRoleByCode = React.useCallback((roleCode) => {
    if (!roleCode) return null;
    return roles.find(r => r.code === roleCode)
      || filterRoles.find(r => r.code === roleCode)
      || resolvedRolesByCode[roleCode]
      || null;
  }, [roles, filterRoles, resolvedRolesByCode]);

  // Get user's primary role from roles list
  const getUserPrimaryRole = React.useCallback((user) => {
    if (!user.primaryRole) return null;
    return getRoleByCode(user.primaryRole);
  }, [getRoleByCode]);

  // Additional roles = user.roles minus primary (resolved to display names when available)
  const getUserAdditionalRoles = React.useCallback((user) => {
    const primary = (user.primaryRole || '').toUpperCase();
    const allCodes = Array.isArray(user.roles) ? user.roles : [];
    return [...new Set(
      allCodes
        .map((code) => String(code || '').trim().toUpperCase())
        .filter(Boolean)
    )]
      .filter((code) => code !== primary)
      .map((code) => {
        const role = getRoleByCode(code);
        return {
          code,
          name: role?.name || code,
          level: role?.level
        };
      });
  }, [getRoleByCode]);

  // Search and filter handlers
  const handleSearchInput = (value) => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  const handleRoleFilter = (roleCode) => {
    const newRoleCode = roleCode === 'all' ? '' : roleCode;
    setFilters({ userRole: newRoleCode });
    updatePagination('users', { current: 1 });
  };

  const selectedFilterRole = useMemo(() => {
    if (!filters?.userRole) {
      return null;
    }

    return roles.find((role) => role.code === filters.userRole)
      || filterRoles.find((role) => role.code === filters.userRole)
      || resolvedRolesByCode[filters.userRole]
      || null;
  }, [filters?.userRole, roles, filterRoles, resolvedRolesByCode]);

  const selectedFilterRoleLabel = selectedFilterRole
    ? `${selectedFilterRole.name}${selectedFilterRole.level ? ` (Level ${selectedFilterRole.level})` : ''}`
    : '';

  const handleStatusFilter = (status) => {
    const newStatus = status === 'all' ? '' : status;
    setFilters({ userStatus: newStatus });
    updatePagination('users', { current: 1 });
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setFilters({ 
      searchTerm: '', 
      userRole: '', 
      userStatus: ''
    });
    updatePagination('users', { current: 1 });
  };

  const handlePageChange = (newPage) => {
    updatePagination('users', { current: newPage });
  };

  const handleLimitChange = (newLimit) => {
    updatePagination('users', { current: 1, limit: newLimit });
  };

  // Users are now filtered and paginated on the server side

  const handleCreateUser = () => {
    setEditingUser(null);
    setView('create');
  };

  const handleEditUser = (user) => {
    if (isAdminOnly && isSuperAdminAccount(user)) {
      return;
    }
    setEditingUser(user);
    setView('edit');
  };

  const handleCancelForm = () => {
    setEditingUser(null);
    const lastView = localStorage.getItem('userManagementView') || 'card';
    setView(lastView);
  };

  const handleFormSuccess = () => {
    handleCancelForm(); // Return to last view
    // Refresh the user list with current pagination settings
    fetchUsers(
      pagination?.users?.current || 1,
      pagination?.users?.limit || 20,
      filters?.searchTerm || '',
      filters?.userRole || '',
      filters?.userStatus || ''
    );
  };

  const handleDeleteUser = async (user) => {
    if (isAdminOnly && isSuperAdminAccount(user)) {
      setShowDeleteConfirm(null);
      return;
    }
    try {
      setDeleteInProgress(true);
      await userAPI.deleteUser(user._id || user.id);
      // Close modal as soon as delete succeeds — do not wait for list refetch (that was making the popup feel "stuck")
      setShowDeleteConfirm(null);
      setDeleteInProgress(false);
      await fetchUsers(
        pagination?.users?.current || 1,
        pagination?.users?.limit || 20,
        filters?.searchTerm || '',
        filters?.userRole || '',
        filters?.userStatus || ''
      );
    } catch (error) {
      console.error('Failed to delete user:', error);
      setDeleteInProgress(false);
    }
  };

  const handleToggleEmailVerification = async (user) => {
    try {
      const newEmailVerified = !user.emailVerified;
      await userAPI.updateUser(user._id || user.id, { 
        emailVerified: newEmailVerified 
      });
      
      // Update the user in the local state
      setUsers(prevUsers => 
        prevUsers.map(u => 
          (u._id || u.id) === (user._id || user.id) 
            ? { ...u, emailVerified: newEmailVerified }
            : u
        )
      );
      
      // Show success message
      if (newEmailVerified) {
        console.log(`Email marked as verified for ${user.email}`);
      } else {
        console.log(`Email marked as unverified for ${user.email}`);
      }
    } catch (error) {
      console.error('Failed to update email verification status:', error);
    }
  };

  const sortedUsers = users || [];

  const getUserIcon = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`;
    }
    if (user.name) {
      const names = user.name.split(' ');
      return names.length > 1 ? `${names[0][0]}${names[names.length - 1][0]}` : names[0][0];
    }
    return user.email?.[0]?.toUpperCase() || '?';
  };

  useEffect(() => {
    if (!additionalRolesMenu) return undefined;

    const handleOutsideClick = (event) => {
      const inMenu = additionalRolesMenuRef.current?.contains(event.target);
      const inTrigger = additionalRolesTriggerRef.current?.contains(event.target);
      if (!inMenu && !inTrigger) {
        setAdditionalRolesMenu(null);
      }
    };

    const handleViewportChange = () => {
      setAdditionalRolesMenu(null);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [additionalRolesMenu]);

  const openAdditionalRolesMenu = (userId, restRoles, triggerEl) => {
    if (!triggerEl) return;
    const rect = triggerEl.getBoundingClientRect();
    const menuWidth = 180;
    const left = Math.min(
      Math.max(8, rect.left),
      window.innerWidth - menuWidth - 8
    );
    setAdditionalRolesMenu({
      userId,
      roles: restRoles,
      top: rect.bottom + 4,
      left,
    });
  };

  const renderAdditionalRolesCell = (user, additionalRoles) => {
    const userId = user._id || user.id;
    if (!additionalRoles.length) {
      return <span className="text-xs text-gray-400 font-medium">—</span>;
    }

    const [firstRole, ...restRoles] = additionalRoles;
    const isOpen = additionalRolesMenu?.userId === userId;

    return (
      <div className="inline-flex items-center gap-1 max-w-[14rem]">
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-100 truncate max-w-[11rem]"
          title={firstRole.name}
        >
          {firstRole.name}
        </span>
        {restRoles.length > 0 && (
          <button
            type="button"
            ref={isOpen ? additionalRolesTriggerRef : null}
            onClick={(e) => {
              e.stopPropagation();
              if (isOpen) {
                setAdditionalRolesMenu(null);
                return;
              }
              openAdditionalRolesMenu(userId, restRoles, e.currentTarget);
            }}
            className={`inline-flex items-center justify-center w-5 h-5 rounded text-gray-500 hover:text-violet-700 hover:bg-violet-50 transition-colors ${
              isOpen ? 'bg-violet-50 text-violet-700' : ''
            }`}
            title={`${restRoles.length} more additional role${restRoles.length === 1 ? '' : 's'}`}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-label="Show more additional roles"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  const additionalRolesDropdownPortal = additionalRolesMenu
    ? createPortal(
        <div
          ref={additionalRolesMenuRef}
          className="fixed z-[100] min-w-[11rem] max-w-[16rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          style={{
            top: additionalRolesMenu.top,
            left: additionalRolesMenu.left,
          }}
          role="listbox"
        >
          <ul className="max-h-48 overflow-auto">
            {additionalRolesMenu.roles.map((role) => (
              <li
                key={role.code}
                className="px-3 py-1.5 text-xs text-gray-700"
                role="option"
              >
                {role.name}
              </li>
            ))}
          </ul>
        </div>,
        document.body
      )
    : null;

  const renderUserRow = (user) => {
    const userName = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name || 'Unknown User';
    const primaryRole = getUserPrimaryRole(user);
    const primaryRoleName = primaryRole?.name || user.primaryRole || 'No role';
    const hasRoleAssigned = Boolean(user.primaryRole);
    const additionalRoles = getUserAdditionalRoles(user);
    const primaryRoleBadgeClass = primaryRole?.level === 1 ? 'bg-red-100 text-red-800' :
      primaryRole?.level === 2 ? 'bg-orange-100 text-orange-800' :
      primaryRole?.level === 3 ? 'bg-yellow-100 text-yellow-800' :
      primaryRole?.level === 4 ? 'bg-blue-100 text-blue-800' :
      'bg-gray-100 text-gray-800';

    return (
      <tr key={user._id || user.id} className="hover:bg-gray-50 transition-colors">
        <td className="px-4 py-0.5 whitespace-nowrap">
          {user.employeeId ? (
            <span className="inline-flex items-center px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded border border-indigo-100">
              {user.employeeId}
            </span>
          ) : (
            <span className="text-xs text-gray-400 font-medium">—</span>
          )}
        </td>
        <td className="px-4 py-0.5 whitespace-nowrap">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-[10px] font-semibold text-indigo-700 flex-shrink-0">
              {getUserIcon(user)}
            </div>
            <div className="flex items-center space-x-2">
              <p className="text-xs font-semibold text-gray-900">{userName}</p>
              {user.id === currentUser?.id && (
                <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full" title="Current User"></span>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 py-0.5 whitespace-nowrap">
          <div className="flex items-center space-x-2">
            <p className="text-xs text-gray-600 font-medium">{user.email}</p>
            {user.emailVerified ? (
              <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" title="Email Verified">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800 flex-shrink-0">
                Unverified
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-0.5 whitespace-nowrap">
          {hasRoleAssigned ? (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${primaryRoleBadgeClass}`}
              title="Primary role"
            >
              {primaryRoleName}
            </span>
          ) : (
            <span className="text-[10px] text-gray-500">No role assigned</span>
          )}
        </td>
        <td className="px-4 py-0.5 whitespace-nowrap">
          {renderAdditionalRolesCell(user, additionalRoles)}
        </td>
        <td className="px-4 py-0.5 whitespace-nowrap">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${user.status === 'active' ? 'bg-green-100 text-green-800' :
              user.status === 'inactive' ? 'bg-red-100 text-red-800' :
                user.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
            }`}>
            {user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : 'Unknown'}
          </span>
        </td>
        <td className="px-4 py-0.5 whitespace-nowrap text-right text-sm font-medium">
          {isAdminOnly && isSuperAdminAccount(user) ? (
            <div className="flex items-center justify-end">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 shadow-sm" title="Super Administrator account is protected">
                <svg className="w-3.5 h-3.5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Protected
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-end space-x-1.5">
              <PermissionGuard permission="USERS:PROFILES:MANAGE">
                <button
                  onClick={() => handleEditUser(user)}
                  className="text-indigo-600 hover:text-indigo-900 p-0.5 hover:bg-indigo-50 rounded transition-all"
                  title="Edit user"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>

                <button
                  onClick={() => handleToggleEmailVerification(user)}
                  className={`p-0.5 rounded transition-all ${
                    user.emailVerified 
                      ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50' 
                      : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                  }`}
                  title={user.emailVerified ? 'Mark email as unverified' : 'Mark email as verified'}
                >
                  {user.emailVerified ? (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18 12M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </button>

                <button
                  onClick={() => setShowLocationModal(user)}
                  className="text-purple-600 hover:text-purple-900 p-0.5 hover:bg-purple-50 rounded transition-all"
                  title="Manage locations"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>

                <button
                  onClick={() => setShowDeleteConfirm(user)}
                  className="text-red-600 hover:text-red-900 p-0.5 hover:bg-red-50 rounded transition-all"
                  title="Delete user"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </PermissionGuard>
            </div>
          )}
        </td>
      </tr>
    );
  };

  const renderUserCard = (user) => {
    const userName = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name || 'Unknown User';
    const primaryRole = getUserPrimaryRole(user);
    const primaryRoleName = primaryRole?.name || user.primaryRole || 'No role';
    const hasRoleAssigned = Boolean(user.primaryRole);
    const additionalRoles = getUserAdditionalRoles(user);

    return (
      <div key={user._id || user.id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group min-h-28 w-full">
        <div className="p-3 h-full flex flex-col">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
                {getUserIcon(user)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center space-x-1">
                  <h3 className="text-sm font-semibold text-gray-900 truncate">
                    {userName}
                  </h3>
                  {user.id === currentUser?.id && (
                    <span className="inline-block w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" title="Current User"></span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 min-w-0">
                  <p className="text-xs text-gray-500 truncate flex-1">{user.email}</p>
                  {user.employeeId && (
                    <span className="inline-flex items-center px-1 py-0.5 bg-indigo-50 text-indigo-700 text-[8px] font-bold rounded border border-indigo-100 shrink-0">
                      {user.employeeId}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {isAdminOnly && isSuperAdminAccount(user) ? (
              <div className="flex items-center space-x-0.5 flex-shrink-0">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200" title="Super Administrator account is protected">
                  <svg className="w-2.5 h-2.5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Protected
                </span>
              </div>
            ) : (
              <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                <PermissionGuard permission="USERS:PROFILES:MANAGE">
                  <button
                    onClick={() => handleEditUser(user)}
                    className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                    title="Edit user"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </PermissionGuard>

                <PermissionGuard permission="USERS:PROFILES:MANAGE">
                  <button
                    onClick={() => handleToggleEmailVerification(user)}
                    className={`p-1 rounded transition-all ${
                      user.emailVerified 
                        ? 'text-orange-400 hover:text-orange-600 hover:bg-orange-50' 
                        : 'text-green-400 hover:text-green-600 hover:bg-green-50'
                    }`}
                    title={user.emailVerified ? 'Mark email as unverified' : 'Mark email as verified'}
                  >
                    {user.emailVerified ? (
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18 12M6 6l12 12" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </button>
                </PermissionGuard>

                <PermissionGuard permission="USERS:PROFILES:MANAGE">
                  <button
                    onClick={() => setShowLocationModal(user)}
                    className="p-1 text-purple-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-all"
                    title="Manage locations"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                </PermissionGuard>

                <PermissionGuard permission="USERS:PROFILES:MANAGE">
                  <button
                    onClick={() => setShowDeleteConfirm(user)}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                    title="Delete user"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </PermissionGuard>
              </div>
            )}
          </div>

          {(hasRoleAssigned || additionalRoles.length > 0) && (
            <div className="mb-2 flex-1 min-w-0">
              {hasRoleAssigned && (
                <p className="text-xs text-gray-600 leading-tight truncate" title={`Primary: ${primaryRoleName}`}>
                  Role: {primaryRoleName}
                </p>
              )}
              {additionalRoles.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1" title="Additional roles">
                  {additionalRoles.map((role) => (
                    <span
                      key={role.code}
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-100"
                    >
                      {role.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-1 mt-auto">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${user.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
                user.status === 'inactive' ? 'bg-red-50 text-red-700 border-red-200' :
                  user.status === 'suspended' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                    'bg-gray-50 text-gray-700 border-gray-200'
              }`}>
              {user.status || 'unknown'}
            </span>
            {user.emailVerified ? (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Verified
              </span>
            ) : (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                Unverified
              </span>
            )}
            {user.id === currentUser?.id && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                You
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const showUsersSkeleton = usersLoading && sortedUsers.length === 0;
  const showUsersOverlay = usersLoading && sortedUsers.length > 0;

  return (
    <div>
      <div className="max-w-7xl mx-auto">
        {/* Error Display */}
        {(error || usersError) && (
          <div className="mb-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-red-800">{error || usersError}</p>
              <button
                onClick={clearError}
                className="ml-auto text-red-600 hover:text-red-500"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        {view === 'edit' && editingUser && (
          <UserEditForm
            user={editingUser}
            onSuccess={handleFormSuccess}
            onCancel={handleCancelForm}
          />
        )}

        {view === 'create' && (
          <UserCreateForm
            onSuccess={handleFormSuccess}
            onCancel={handleCancelForm}
          />
        )}

        {(view === 'card' || view === 'list') && (
          <div className="bg-white shadow-sm border border-gray-200">
            {/* Filters and Search */}
            <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0">
              <div className="flex flex-col gap-2 min-w-0">
                {/* Search */}
                <div className="w-full min-w-0">
                  <TextInput
                    variant="input"
                    type="text"
                    placeholder="Search users by name or email..."
                    value={searchInput}
                    onChange={(e) => handleSearchInput(e.target.value)}
                    icon={({ className }) => (
                      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                    className="text-sm py-1.5 border border-gray-300 shadow-sm !rounded-sm"
                  />
                </div>                {/* Filters and Actions */}
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  {/* Role Filter */}
                  <div className="min-w-[180px]">
                    <SearchableRoleDropdown
                      value={filters?.userRole || ''}
                      onChange={(roleCode) => handleRoleFilter(roleCode)}
                      displayLabel={selectedFilterRoleLabel}
                      placeholder="All Roles"
                      limit={50}
                      menuPlacement="down"
                    />
                  </div>

                  {/* Status Filter */}
                  <CustomSelect
                    value={filters?.userStatus || 'all'}
                    onChange={(e) => handleStatusFilter(e.target.value)}
                    options={[
                      { value: 'all', label: 'All Status' },
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                      { value: 'expired', label: 'Expired' }
                    ]}
                    size="sm"
                    variant="default"
                    className="min-w-[120px]"
                  />

                  {/* Reset Filters Button */}
                  {(searchInput || filters?.userRole || filters?.userStatus) && (
                    <button
                      onClick={handleResetFilters}
                      className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-all"
                      title="Reset all filters"
                    >
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Reset
                    </button>
                  )}

                  {/* View Toggle */}
                  <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                    <button
                      onClick={() => handleViewChange('card')}
                      className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card'
                        ? 'bg-white text-indigo-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                      title="Card view"
                    >
                      <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                      </svg>
                      Cards
                    </button>
                    <button
                      onClick={() => handleViewChange('list')}
                      className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'list'
                        ? 'bg-white text-indigo-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                      title="List view"
                    >
                      <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      List
                    </button>
                  </div>

                  {/* Add User Button */}
                  <PermissionGuard permission="USERS:PROFILES:CREATE">
                    <button
                      onClick={handleCreateUser}
                      className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                    >
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add User
                    </button>
                  </PermissionGuard>
                </div>
              </div>
            </div>

            {/* Modern Pagination Section */}
            {pagination?.users && (
              <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 border-b border-gray-200 flex-shrink-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                        </svg>
                      </div>
                      <div className="text-sm flex items-center gap-2">
                        <div>
                          <span className="font-semibold text-gray-900">
                            {((pagination.users.current - 1) * pagination.users.limit) + 1}-{Math.min(pagination.users.current * pagination.users.limit, pagination.users.total)}
                          </span>
                          <span className="text-gray-500 mx-1">of</span>
                          <span className="font-semibold text-gray-900">{pagination.users.total}</span>
                          <span className="text-gray-500 ml-1">users</span>
                        </div>
                        {(usersLoading) && sortedUsers.length > 0 && (
                          <div className="flex items-center">
                            <svg className="animate-spin h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Navigation Controls */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 font-medium">Show:</span>
                      <CustomSelect
                        id="limit"
                        value={pagination.users.limit}
                        onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                        disabled={usersLoading}
                        options={[
                          { value: 10, label: '10' },
                          { value: 20, label: '20' },
                          { value: 50, label: '50' },
                          { value: 100, label: '100' }
                        ]}
                        size="sm"
                        variant="minimal"
                        className="min-w-[70px]"
                      />
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.users.current <= 1 || usersLoading}
                        className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        title="First page"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                        </svg>
                      </button>

                      <button
                        onClick={() => handlePageChange(Math.max(1, pagination.users.current - 1))}
                        disabled={pagination.users.current <= 1 || usersLoading}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span className="hidden sm:inline">Previous</span>
                      </button>

                      {/* Page Numbers */}
                      <div className="hidden md:flex items-center gap-1">
                        {(() => {
                          const currentPage = pagination.users.current;
                          const totalPages = pagination.users.pages;
                          const pages = [];
                          
                          // Always show first page
                          if (totalPages > 0) {
                            pages.push(
                              <button
                                key={1}
                                onClick={() => handlePageChange(1)}
                                disabled={usersLoading}
                                className={`px-3 py-1.5 text-sm font-medium rounded transition-all ${
                                  currentPage === 1
                                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm'
                                    : 'text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                1
                              </button>
                            );
                          }

                          // Show ellipsis if needed
                          if (currentPage > 3 && totalPages > 5) {
                            pages.push(
                              <span key="ellipsis-1" className="px-2 text-gray-400">
                                ...
                              </span>
                            );
                          }

                          // Show pages around current page
                          const startPage = Math.max(2, currentPage - 1);
                          const endPage = Math.min(totalPages - 1, currentPage + 1);

                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <button
                                key={i}
                                onClick={() => handlePageChange(i)}
                                disabled={usersLoading}
                                className={`px-3 py-1.5 text-sm font-medium rounded transition-all ${
                                  currentPage === i
                                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm'
                                    : 'text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                {i}
                              </button>
                            );
                          }

                          // Show ellipsis if needed
                          if (currentPage < totalPages - 2 && totalPages > 5) {
                            pages.push(
                              <span key="ellipsis-2" className="px-2 text-gray-400">
                                ...
                              </span>
                            );
                          }

                          // Always show last page if more than 1 page
                          if (totalPages > 1) {
                            pages.push(
                              <button
                                key={totalPages}
                                onClick={() => handlePageChange(totalPages)}
                                disabled={usersLoading}
                                className={`px-3 py-1.5 text-sm font-medium rounded transition-all ${
                                  currentPage === totalPages
                                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm'
                                    : 'text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                {totalPages}
                              </button>
                            );
                          }

                          return pages;
                        })()}
                      </div>

                      <button
                        onClick={() => handlePageChange(Math.min(pagination.users.pages, pagination.users.current + 1))}
                        disabled={pagination.users.current >= pagination.users.pages || usersLoading}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                      >
                        <span className="hidden sm:inline">Next</span>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>

                      <button
                        onClick={() => handlePageChange(pagination.users.pages)}
                        disabled={pagination.users.current >= pagination.users.pages || usersLoading}
                        className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        title="Last page"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Mobile Page Numbers */}
                <div className="flex md:hidden items-center justify-center gap-1 mt-3 pt-3 border-t border-gray-200">
                  {Array.from({ length: Math.min(7, pagination.users.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.users.pages <= 7) {
                      pageNum = i + 1;
                    } else if (pagination.users.current <= 4) {
                      pageNum = i + 1;
                    } else if (pagination.users.current >= pagination.users.pages - 3) {
                      pageNum = pagination.users.pages - 6 + i;
                    } else {
                      pageNum = pagination.users.current - 3 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        disabled={usersLoading}
                        className={`min-w-[32px] h-8 rounded text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${pageNum === pagination.users.current
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md shadow-indigo-500/20'
                            : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Content */}
            <div className="p-2">
              {showUsersSkeleton ? (
                view === 'list' ? (
                  <TableSkeleton
                    columns={7}
                    rows={Math.min(pagination?.users?.limit || 20, 10)}
                    columnWidths={['w-16', 'w-28', 'w-36', 'w-24', 'w-24', 'w-14', 'w-16']}
                  />
                ) : (
                  <CardGridSkeleton count={8} />
                )
              ) : sortedUsers.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                  <p className="text-sm text-gray-500">
                    {searchInput || filters?.userRole || filters?.userStatus ?
                      'Try adjusting your search or filter criteria.' :
                      'Get started by creating your first user.'
                    }
                  </p>
                  {(!searchInput && !filters?.userRole && !filters?.userStatus) && (
                    <div className="mt-6">
                      <PermissionGuard permission="USERS:PROFILES:CREATE">
                        <button
                          onClick={handleCreateUser}
                          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Create User
                        </button>
                      </PermissionGuard>
                    </div>
                  )}
                </div>
              ) : (
                <DataViewLoadingContainer
                  loading={showUsersOverlay}
                  message={view === 'list' ? 'Loading users…' : 'Loading…'}
                >
                  {/* Card View */}
                  {view === 'card' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                      {sortedUsers.map(renderUserCard)}
                    </div>
                  )}

                  {/* List View */}
                  {view === 'list' && (
                    <div className="overflow-x-auto max-h-[70vh]">
                      <table className="min-w-max w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Emp No
                            </th>
                            <th className="px-4 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              User
                            </th>
                            <th className="px-4 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Mail
                            </th>
                            <th className="px-4 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Role
                            </th>
                            <th className="px-4 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Additional Roles
                            </th>
                            <th className="px-4 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-4 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {sortedUsers.map(renderUserRow)}
                        </tbody>
                      </table>
                    </div>
                  )}
                </DataViewLoadingContainer>
              )}
            </div>
          </div>
        )}

        {/* Bottom Pagination */}
        {pagination?.users && (
          <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 border-t border-gray-200 flex-shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                  </svg>
                </div>
                <div className="text-sm">
                  <span className="font-semibold text-gray-900">{((pagination.users.current - 1) * pagination.users.limit) + 1}-{Math.min(pagination.users.current * pagination.users.limit, pagination.users.total)}</span>
                  <span className="text-gray-500 mx-1">of</span>
                  <span className="font-semibold text-gray-900">{pagination.users.total}</span>
                  <span className="text-gray-500 ml-1">users</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => handlePageChange(1)} disabled={pagination.users.current <= 1 || usersLoading} className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all" title="First page">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                </button>
                <button onClick={() => handlePageChange(Math.max(1, pagination.users.current - 1))} disabled={pagination.users.current <= 1 || usersLoading} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  <span className="hidden sm:inline">Previous</span>
                </button>
                <div className="hidden md:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.users.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.users.pages <= 5) pageNum = i + 1;
                    else if (pagination.users.current <= 3) pageNum = i + 1;
                    else if (pagination.users.current >= pagination.users.pages - 2) pageNum = pagination.users.pages - 4 + i;
                    else pageNum = pagination.users.current - 2 + i;
                    return (
                      <button key={pageNum} onClick={() => handlePageChange(pageNum)} disabled={usersLoading} className={`min-w-[32px] h-8 rounded text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${pageNum === pagination.users.current ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md shadow-indigo-500/20' : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'}`}>{pageNum}</button>
                    );
                  })}
                </div>
                <button onClick={() => handlePageChange(Math.min(pagination.users.pages, pagination.users.current + 1))} disabled={pagination.users.current >= pagination.users.pages || usersLoading} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm">
                  <span className="hidden sm:inline">Next</span>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
                <button onClick={() => handlePageChange(pagination.users.pages)} disabled={pagination.users.current >= pagination.users.pages || usersLoading} className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all" title="Last page">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded shadow-2xl border border-gray-100 w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded mb-4">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>

                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete User</h3>
                  <p className="text-sm text-gray-600">
                    Are you sure you want to delete <span className="font-medium text-gray-900">"{showDeleteConfirm.firstName && showDeleteConfirm.lastName ? `${showDeleteConfirm.firstName} ${showDeleteConfirm.lastName}` : showDeleteConfirm.name || showDeleteConfirm.email}"</span>?
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowDeleteConfirm(null); setDeleteInProgress(false); }}
                    disabled={deleteInProgress}
                    className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteUser(showDeleteConfirm)}
                    disabled={deleteInProgress}
                    className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded transition-colors disabled:opacity-70 disabled:cursor-wait"
                  >
                    {deleteInProgress ? 'Deleting…' : 'Delete User'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Locations Modal */}
        {showLocationModal && (
          <UserLocationManagement
            user={showLocationModal}
            onClose={() => setShowLocationModal(null)}
            onSuccess={() => fetchUsers(
              pagination?.users?.current || 1,
              pagination?.users?.limit || 20,
              filters?.searchTerm || '',
              filters?.userRole || '',
              filters?.userStatus || ''
            )}
          />
        )}

        {additionalRolesDropdownPortal}
      </div>
    </div>
  );
};

export default UserManagement;